import React, { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { AlertCircle, CheckCircle2, Clock, Loader2, Plus, TrendingDown, Zap } from "lucide-react";
import {
  ACCENT, MIGRAINE_TYPES, MIGRAINE_TRIGGER_LIST,
  fmtDate, humanize, Field, SummaryCard, SeverityBadge, HistoryTable,
} from "./shared";

export default function MigraineTab({ data, onSaved }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    migraine_type: "migraine_without_aura",
    pain_intensity_0_10: 6,
    duration_hours: 4,
    aura: false,
    disability_level: "moderate",
    triggers: [],
    abortive_taken: "",
    relief_within_2h: null,
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  function toggleTrigger(t) {
    setForm((f) => ({
      ...f,
      triggers: f.triggers.includes(t) ? f.triggers.filter((x) => x !== t) : [...f.triggers, t],
    }));
  }

  async function submit(e) {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      await api.post("/specialties/neurology/migraine", {
        ...form,
        pain_intensity_0_10: Number(form.pain_intensity_0_10),
        duration_hours: Number(form.duration_hours),
        abortive_taken: form.abortive_taken || null,
        notes: form.notes || null,
      });
      toast.success("Migraine episode logged");
      setForm((f) => ({ ...f,
        pain_intensity_0_10: 6, duration_hours: 4, aura: false,
        triggers: [], abortive_taken: "", relief_within_2h: null, notes: "",
      }));
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to log episode");
    } finally { setSaving(false); }
  }

  const barData = useMemo(
    () => [...data.episodes].slice().reverse().map((e) => ({
      date: fmtDate(e.date), Pain: e.pain_intensity_0_10, Duration: e.duration_hours,
    })),
    [data.episodes]
  );

  return (
    <div className="space-y-6" data-testid="neuro-migraine-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="migraine-summary">
        <SummaryCard title="Episodes (30d)" value={data.summary?.count_last_30d ?? 0} icon={Zap} />
        <SummaryCard title="Avg pain (30d)"
                     value={data.summary?.avg_pain_last_30d != null
                       ? `${data.summary.avg_pain_last_30d} / 10` : "—"}
                     icon={TrendingDown} />
        <SummaryCard title="Total logged" value={data.episodes?.length ?? 0} icon={Clock} />
      </div>

      <form onSubmit={submit} className="ros-card space-y-4" data-testid="migraine-form">
        <div className="font-heading text-lg">Log a migraine episode</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Date">
            <input type="date" value={form.date}
                   onChange={(e) => setForm({ ...form, date: e.target.value })}
                   data-testid="migraine-input-date" className="ros-input" required />
          </Field>
          <Field label="Type">
            <select value={form.migraine_type}
                    onChange={(e) => setForm({ ...form, migraine_type: e.target.value })}
                    data-testid="migraine-input-type" className="ros-input">
              {MIGRAINE_TYPES.map((t) => <option key={t} value={t}>{humanize(t)}</option>)}
            </select>
          </Field>
          <Field label="Duration (hours)">
            <input type="number" step="0.5" min={0} max={168} value={form.duration_hours}
                   onChange={(e) => setForm({ ...form, duration_hours: e.target.value })}
                   data-testid="migraine-input-duration" className="ros-input" required />
          </Field>
        </div>

        <Field label={`Pain intensity: ${form.pain_intensity_0_10} / 10`}>
          <input type="range" min={0} max={10} value={form.pain_intensity_0_10}
                 onChange={(e) => setForm({ ...form, pain_intensity_0_10: e.target.value })}
                 data-testid="migraine-input-pain"
                 className="w-full accent-[#A78BFA]" />
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Disability level">
            <select value={form.disability_level}
                    onChange={(e) => setForm({ ...form, disability_level: e.target.value })}
                    data-testid="migraine-input-disability" className="ros-input">
              <option value="none">None</option>
              <option value="mild">Mild</option>
              <option value="moderate">Moderate</option>
              <option value="severe">Severe (bed-ridden)</option>
            </select>
          </Field>
          <Field label="Abortive taken">
            <input type="text" value={form.abortive_taken}
                   onChange={(e) => setForm({ ...form, abortive_taken: e.target.value })}
                   data-testid="migraine-input-abortive"
                   placeholder="Sumatriptan 50mg" className="ros-input" />
          </Field>
        </div>

        <label className="inline-flex items-center gap-2 cursor-pointer text-white text-sm">
          <input type="checkbox" checked={form.aura}
                 onChange={(e) => setForm({ ...form, aura: e.target.checked })}
                 data-testid="migraine-input-aura"
                 className="accent-[#A78BFA]" />
          Aura present
        </label>

        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7] mb-2">Triggers</div>
          <div className="flex flex-wrap gap-2" data-testid="migraine-triggers">
            {MIGRAINE_TRIGGER_LIST.map((t) => {
              const on = form.triggers.includes(t);
              return (
                <button type="button" key={t} onClick={() => toggleTrigger(t)}
                        data-testid={`migraine-trigger-${t}`}
                        className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                          on ? "text-black border-transparent" : "text-[#A3ACA7] border-[#272E2B] hover:text-white"
                        }`}
                        style={on ? { background: ACCENT } : {}}>
                  {humanize(t)}
                </button>
              );
            })}
          </div>
        </div>

        {error && (
          <div className="text-sm text-[#F87171] flex items-center gap-2" data-testid="migraine-error">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <div>
          <button type="submit" disabled={saving}
                  data-testid="migraine-submit"
                  className="flex items-center gap-2 px-5 py-2 rounded-full text-black font-semibold disabled:opacity-50 transition-colors"
                  style={{ background: ACCENT }}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {saving ? "Logging…" : "Log episode"}
          </button>
        </div>
      </form>

      {barData.length > 0 && (
        <div className="ros-card" data-testid="migraine-chart">
          <div className="font-heading text-lg mb-3">Pain × Duration</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData} margin={{ top: 10, right: 16, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#272E2B" />
                <XAxis dataKey="date" stroke="#A3ACA7" fontSize={11} />
                <YAxis stroke="#A3ACA7" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0F1312", border: "1px solid #272E2B", borderRadius: 12 }} />
                <Bar dataKey="Pain"     fill="#A78BFA" />
                <Bar dataKey="Duration" fill="#60A5FA" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <HistoryTable
        title={`Episodes (${data.episodes.length})`}
        testId="migraine-history"
        rows={data.episodes}
        rowTestId={(r) => `migraine-row-${r.id}`}
        empty="No migraine episodes logged."
        columns={[
          { label: "Date", render: (r) => fmtDate(r.date) },
          { label: "Type", render: (r) => <span className="text-xs">{humanize(r.migraine_type)}</span> },
          { label: "Pain", render: (r) => <span className="font-mono">{r.pain_intensity_0_10}/10</span> },
          { label: "Dur", render: (r) => <span className="font-mono">{r.duration_hours}h</span> },
          { label: "Aura", render: (r) => r.aura ? <CheckCircle2 size={14} style={{ color: ACCENT }} /> : <span className="text-[#7A8480]">—</span> },
          { label: "Severity", render: (r) => <SeverityBadge value={r.severity} /> },
          { label: "Triggers", render: (r) => (
            <div className="flex flex-wrap gap-1">
              {(r.triggers || []).slice(0, 3).map((t) =>
                <span key={t} className="text-[10px] text-[#A3ACA7] bg-[#1C201E] px-1.5 py-0.5 rounded">{humanize(t)}</span>
              )}
            </div>
          )},
        ]}
      />
    </div>
  );
}
